import { provideHttpClient, withFetch } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  Router,
  TitleStrategy,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import * as Sentry from '@sentry/angular';
import { providePrimeNG } from 'primeng/config';
import { AuthModule } from '@auth0/auth0-angular';
import { appRoutes, AUTH_REDIRECT_URI } from './app.routes';
import { ApiAngularSdk, OpenAPI } from '@zagops/api/sdk-angular';
import { Zagops } from './assets/themes/zagops';
import { MessageService } from 'primeng/api';
import { PrefixTitleStrategy } from './app/services/prefix-title-strategy.service';
import type { WebRuntimeConfig } from './runtime-config';

export function createAppConfig(
  config: Readonly<WebRuntimeConfig>,
): ApplicationConfig {
  return {
    providers: [
      provideRouter(
        appRoutes,
        withInMemoryScrolling({
          anchorScrolling: 'enabled',
          scrollPositionRestoration: 'enabled',
        }),
        withEnabledBlockingInitialNavigation(),
      ),
      provideHttpClient(withFetch()),
      provideAnimationsAsync(),
      providePrimeNG({
        theme: { preset: Zagops, options: { darkModeSelector: '.app-dark' } },
      }),
      importProvidersFrom([
        ApiAngularSdk,
        AuthModule.forRoot({
          domain: config.auth0Domain,
          clientId: config.auth0ClientId,
          authorizationParams: {
            redirect_uri: `${window.location.origin}/${AUTH_REDIRECT_URI}`,
            audience: config.auth0ApiAudience,
          },
        }),
      ]),
      { provide: TitleStrategy, useClass: PrefixTitleStrategy },
      { provide: OpenAPI, useValue: { ...OpenAPI, BASE: config.apiUrl } },
      MessageService,
      { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
      { provide: Sentry.TraceService, deps: [Router] },
      provideAppInitializer(() => {
        inject(Sentry.TraceService);
      }),
    ],
  };
}
